/**
 * Barra inferior unificada del detalle de Historia. Mantiene siempre montado el
 * mismo botón principal para poder animar las transiciones con Framer.
 *
 * Tres estados visuales:
 *   1. intro                     → botón «Empezar» a ancho completo.
 *   2. consuming + audio sonando → el mismo botón se transforma:
 *        - el label «Empezar» se funde,
 *        - el ancho se encoge a ~48 px,
 *        - en paralelo el botón se centra horizontalmente,
 *        - cuando termina el fade-out entra el icono ▶.
 *   3. consuming + audio terminado → el play se desplaza a la izquierda y
 *      «Empezar actividades» aparece deslizándose desde la derecha.
 *
 * Para historias sin audio el flujo es más sencillo: el mismo botón cambia el
 * label de «Empezar» a «Empezar actividades» (cross-fade en el mismo sitio,
 * sin morph de ancho ni segundo botón).
 *
 * El play/pause y el flag `hasAudioFinished` los lee de `StoryAudioContext`,
 * donde el `KaraokePlayer` se registra automáticamente al montar.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { PlaybackSpeedDrawer } from '@/components/molecules/PlaybackSpeedDrawer';
import { AUDIO_SPEED_OPTIONS } from '@/constants/audio';
import { useStoryAudio } from '@/context/StoryAudioContext';
import { cn } from '@/utils/cn';
import styles from '../StoryPage/StoryPage.module.scss';

export interface StoryMorphBarProps {
  phase: 'intro' | 'consuming';
  hasAudio: boolean;
  onEmpezar: () => void;
  onActivities: () => void;
}

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const EASE = [0.4, 0, 0.2, 1] as const;

/**
 * Coreografía intro → consuming (con audio) — todas las etapas secuenciales:
 *
 *   ┌──────────┬──────────┬───────────────┬──────────┬──────────────────────────┐
 *   │ 0…200 ms │ 200…400  │ 400 … 750 ms  │ 750…900  │ 900 ms … (con rebote)    │
 *   │ label out│ gap 1    │ width shrink  │ gap 2    │ scale 0→1 spring +       │
 *   │          │ (200 ms) │ (cuadrado     │ (150 ms) │ radius rectángulo→círculo│
 *   │          │          │  48×48 px)    │          │                          │
 *   └──────────┴──────────┴───────────────┴──────────┴──────────────────────────┘
 *
 * El segundo gap garantiza que el alumno perciba primero que el botón llega
 * al tamaño del play (rectangular), y solo después se redondee y entre el
 * icono — son dos beats, no uno simultáneo.
 */
const FADE_OUT_S = 0.2;
const LABEL_TO_LAYOUT_GAP_S = 0.2;
const LAYOUT_DELAY_S = FADE_OUT_S + LABEL_TO_LAYOUT_GAP_S; // 0.4
const WIDTH_S = 0.35;
const WIDTH_TO_ROUND_GAP_S = 0.15;
const ICON_DELAY_S = LAYOUT_DELAY_S + WIDTH_S + WIDTH_TO_ROUND_GAP_S; // 0.9
const ACTIVITIES_DURATION_S = 0.32;
/** Margen para que el spring del icono ▶ asiente antes de dar por acabado el morph. */
const ICON_SETTLE_S = 0.45;

// Pop de entrada del botón de velocidad: mismo rebote que el CTA redondo del
// Banner (scale 0 → 1.2 → 1 en 400ms, keyframe repartido 60/40). Requiere que
// el elemento NO tenga transición CSS sobre `transform` (pelearía con Framer).
// Sin delay propio: el gate de `introMorphSettled` (ICON_SETTLE_S) ya deja
// ~100-150ms tras el aterrizaje visual del play — beat suficiente sin que se
// perciba espera.
const SPEED_POP_SCALE = [0, 1.2, 1];
const SPEED_POP: Transition = { duration: 0.4, times: [0, 0.6, 1], ease: 'easeOut' };

type LabelKey = 'empezar' | 'play' | 'actividades';

export function StoryMorphBar({
  phase,
  hasAudio,
  onEmpezar,
  onActivities,
}: StoryMorphBarProps) {
  const { t } = useTranslation();
  const reducedMotion = Boolean(useReducedMotion());
  const audioCtx = useStoryAudio();
  const playPause = audioCtx?.playPause;
  const isPlaying = audioCtx?.isPlaying ?? false;
  const audioFinished = audioCtx?.hasAudioFinished ?? false;
  const playbackSpeed = audioCtx?.playbackSpeed ?? 1;
  const setPlaybackSpeed = audioCtx?.setPlaybackSpeed;
  const [speedDrawerOpen, setSpeedDrawerOpen] = useState(false);

  const isConsuming = phase === 'consuming';
  /** Modo icono: el slot principal pasa a 48 px y muestra play/pause. */
  const inIconMode = isConsuming && hasAudio;

  /**
   * ¿Ha terminado la coreografía intro → icono? Con audio ya escuchado de una
   * sesión anterior, `audioFinished` es true desde el montaje del karaoke: sin
   * esta retención, «Empezar actividades» entraría pisando el fade-out del
   * «Empezar». Retenemos la fase 3 hasta que el morph completo (label out +
   * shrink + pop del icono) haya asentado; en el flujo normal el audio termina
   * minutos después, así que este gate ya está abierto y no cambia nada.
   */
  const [introMorphSettled, setIntroMorphSettled] = useState(false);
  useEffect(() => {
    if (!inIconMode) {
      setIntroMorphSettled(false);
      return;
    }
    const tid = setTimeout(
      () => setIntroMorphSettled(true),
      (ICON_DELAY_S + ICON_SETTLE_S) * 1000
    );
    return () => clearTimeout(tid);
  }, [inIconMode]);

  /** Mientras el audio aún no ha terminado, el icono play va solo y centrado. */
  const centerSlot = inIconMode && !audioFinished;
  /** Cuando el audio termina (y el morph de entrada asentó), aparece «Empezar actividades» como hermano. */
  const showActivitiesSibling = inIconMode && audioFinished && introMorphSettled;

  const labelKey: LabelKey = !isConsuming ? 'empezar' : inIconMode ? 'play' : 'actividades';

  /**
   * Centramos el slot del play durante la fase 2 (audio sonando) usando un
   * translate por JS — nada de FLIP/`layout` (cualquier scale heredado por
   * los hijos los deformaría). Medimos el ancho del contenedor y calculamos
   * cuánto hay que trasladar el slot (que mide 48 px) hasta el centro.
   */
  const startBarRef = useRef<HTMLDivElement>(null);
  const [centerOffset, setCenterOffset] = useState(0);

  useLayoutEffect(() => {
    const node = startBarRef.current;
    if (!node) return;
    const measure = () => {
      setCenterOffset(Math.max(0, (node.offsetWidth - 48) / 2));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const slotX = centerSlot ? centerOffset : 0;
  /**
   * En el último beat de la coreografía (al mismo tiempo que el icono entra
   * con el rebote y el border-radius termina de redondear) ampliamos el
   * botón a 1.2× para enfatizar que ya está listo. Cuando el audio termina y
   * aparece «Empezar actividades», volvemos a escala 1 para que el layout
   * con el hermano cuadre.
   */
  const slotScale = inIconMode && !audioFinished ? 1.2 : 1;

  /**
   * Solo aplicamos delays largos en la PRIMERA entrada a `inIconMode` (es
   * decir, intro → state 2): el alumno necesita ver primero el label fade-
   * out, luego el shrink, luego el rebote del icono. En transiciones
   * subsecuentes (state 2 ⇄ state 3 al pulsar play o al terminar el audio)
   * el cambio debe ser inmediato — sin delay — porque el botón ya está en
   * modo icono y solo cambia su posición/escala.
   */
  const wasIconModeRef = useRef(inIconMode);
  const isFirstMorph = inIconMode && !wasIconModeRef.current;
  useEffect(() => {
    wasIconModeRef.current = inIconMode;
  });
  const xDelay = isFirstMorph ? LAYOUT_DELAY_S : 0;
  const scaleDelay = isFirstMorph ? ICON_DELAY_S : 0;

  const handleMorphClick = () => {
    if (inIconMode) {
      playPause?.();
      return;
    }
    if (isConsuming) {
      onActivities();
      return;
    }
    onEmpezar();
  };

  const morphAriaLabel = inIconMode
    ? isPlaying
      ? t('story.pause')
      : t('story.play')
    : undefined;

  return (
    <div
      ref={startBarRef}
      className={styles.story__startBar}
    >
      {/**
       * Sin `layout` (FLIP) → no hay `transform: scaleX` aplicado al slot.
       * El ancho lo anima CSS (transition sobre `width` con delay) y la
       * posición la anima Framer con un `x` (translate, no scale, no
       * deforma a los hijos).
       */}
      <motion.div
        initial={false}
        animate={{ x: slotX, scale: slotScale }}
        transition={{
          x: { duration: WIDTH_S, ease: EASE, delay: xDelay },
          scale: { duration: 0.42, ease: EASE, delay: scaleDelay },
        }}
        className={cn(
          styles.story__morphSlot,
          inIconMode && styles['story__morphSlot--icon']
        )}
      >
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className={styles.story__portalButton}
          onClick={handleMorphClick}
          aria-label={morphAriaLabel}
        >
          {/**
           * AnimatePresence + mode="wait" garantiza que solo vive en el DOM
           * el rótulo activo: el «Empezar» se desmonta cuando termina su
           * fade-out, y solo entonces se monta el icono ▶ (en estado scale 0,
           * opacity 0). Así nada se queda visible mientras el ancho del slot
           * encoge.
           */}
          <span className={styles.story__morphContent}>
            <AnimatePresence mode="wait" initial={false}>
              {labelKey === 'empezar' && (
                <motion.span
                  key="empezar"
                  className={styles.story__morphContentLayer}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, transition: { duration: FADE_OUT_S, ease: EASE } }}
                >
                  {t('activities.start', 'Empezar')}
                </motion.span>
              )}
              {labelKey === 'play' && (
                <motion.span
                  key="play"
                  className={styles.story__morphContentLayer}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0, transition: { duration: 0.16, ease: EASE } }}
                  transition={{
                    // Mount ocurre al terminar el exit del «Empezar» (≈ 0.2s).
                    // El delay aquí es relativo al mount: arranca el rebote
                    // cuando el ancho ya ha terminado de encoger.
                    delay: ICON_DELAY_S - FADE_OUT_S,
                    type: 'spring',
                    stiffness: 360,
                    damping: 13,
                    mass: 0.7,
                  }}
                >
                  {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </motion.span>
              )}
              {labelKey === 'actividades' && (
                <motion.span
                  key="actividades"
                  className={styles.story__morphContentLayer}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, transition: { duration: 0.14, ease: EASE } }}
                  transition={{ duration: 0.18, ease: EASE }}
                >
                  {t('story.startActivities')}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </Button>
      </motion.div>

      {/**
       * Botón de velocidad: solo mientras SUENA el audio con el play centrado
       * (pausar o terminar lo retira) y una vez la coreografía intro → play ha
       * ASENTADO — con autoplay el audio arranca en mitad del morph, y sin esa
       * espera el pop entraría pisándolo (el rebote se pierde entre ambas
       * animaciones). Mismo beat que el Banner: primero asienta lo principal,
       * después rebota el secundario.
       *
       * Un único elemento motion como hijo directo de AnimatePresence (si no,
       * la animación de salida no corre): la posición la da CSS con left/top
       * (propiedades que no tocan `transform`) y la escala la anima Framer —
       * sin transición CSS de transform en este elemento (conflicto Banner).
       */}
      <AnimatePresence>
        {centerSlot && isPlaying && introMorphSettled && (
          <motion.span
            key="speed-button"
            className={styles.story__speedButtonWrap}
            initial={{ scale: 0 }}
            animate={{ scale: reducedMotion ? 1 : SPEED_POP_SCALE }}
            exit={{ scale: 0, transition: { duration: reducedMotion ? 0 : 0.18, ease: EASE } }}
            transition={reducedMotion ? { duration: 0 } : SPEED_POP}
          >
            <IconButton
              icon={Settings}
              variant="primary"
              buttonSize="md"
              className={styles.story__speedButton}
              aria-label={t('story.playbackSpeedLabel')}
              // Pausamos al abrir: elegir velocidad con el audio corriendo
              // desorienta (el karaoke sigue avanzando bajo el overlay). Al
              // cerrar el drawer se reanuda solo desde el mismo punto.
              onClick={() => {
                if (isPlaying) playPause?.();
                setSpeedDrawerOpen(true);
              }}
            />
          </motion.span>
        )}
      </AnimatePresence>

      {setPlaybackSpeed && (
        <PlaybackSpeedDrawer
          open={speedDrawerOpen}
          // Al cerrar (X, swipe, tocar fuera) el karaoke continúa donde se
          // pausó al abrir — la pausa no pierde la posición y el gear solo
          // existe con el audio sonando, así que reanudar siempre es correcto.
          onOpenChange={(open) => {
            setSpeedDrawerOpen(open);
            if (!open) playPause?.();
          }}
          speed={playbackSpeed}
          options={AUDIO_SPEED_OPTIONS}
          onSpeedChange={setPlaybackSpeed}
        />
      )}

      <AnimatePresence>
        {showActivitiesSibling && (
          <motion.div
            key="activities-sibling"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16, transition: { duration: 0.22, ease: EASE } }}
            transition={{ duration: ACTIVITIES_DURATION_S, ease: EASE }}
            className={styles.story__startBarPortalMotion}
          >
            <Button
              variant="primary"
              size="lg"
              fullWidth
              className={styles.story__portalButton}
              onClick={onActivities}
            >
              {t('story.startActivities')}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
